/**
 * CaptureLoader.tsx — "Setting up your health check": what Step 0 shows, in
 * place of its form, from the moment "Check my eligibility" passes validation
 * until the projection chart replaces it.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: journey look-and-feel,
 * loaders (CTO, 2026-09-25 — "When you click 'Check my eligibility,' that
 * button hangs there … there is an interesting way to say … we are checking
 * your responses … and then take the user to the chart").
 *
 * WHY: on stage the lead POST can sit behind a cold service for seconds (a
 * minute at worst), and the only sign of life was "Checking…" on a button — it
 * read as a hang. This is the checking loader's language (the shared
 * `NarratedChecklist`, same marks, same motion, same one status region) with
 * Step 0's own three steps. WHAT each step says and WHEN it may tick is
 * `journeyLoaders.ts` section 3 (`runCaptureBeat`); this file only draws the
 * view it is handed.
 *
 * SAME COLUMN, NO ROUTE CHANGE. CaptureStep renders this instead of its form —
 * the stage is still `capture`, so nothing about the URL, the history or the
 * funnel moves until the chart arrives.
 *
 * FOCUS AND SCROLL. The control the patient pressed is gone the instant this
 * mounts, so focus goes to the heading (`focusTitle`) and the page returns to
 * the top: on a phone the submit is pressed from a sticky bar at the foot of a
 * long form, and a short screen swapped in under a scrolled page would open
 * part-way down. Instant, never smooth — the content has already been
 * replaced, so a glide would show nothing but a moving page.
 *
 * ⚠️ PUBLIC, PRE-ELIGIBILITY. Every line is swept against R28 and the Art 9
 * list (`journeyLoaders.spec.ts`, `r28.spec.tsx`). The eyebrow greets with the
 * first name typed on this very screen — in memory only, through the Result
 * screen's `greetingName`, which drops anything that does not read as a given
 * name — and never with anything fetched.
 */
import { useEffect } from 'react';
import { JourneyScreen } from './JourneyScreen';
import { NarratedChecklist } from './NarratedChecklist';
import { CAPTURE_COPY, CAPTURE_STEPS, captureThanks, type CaptureBeatView } from './journeyLoaders';
import { greetingName } from './resultCopy';

/** The save step is drawn "missed" only when the POST has answered with a failure. */
const SAVE_MISSED: ReadonlyArray<string> = ['saving'];
const NOTHING_MISSED: ReadonlyArray<string> = [];

/** The late line for a view: the calmer 12s line wins over the 4s one. */
export function captureWaitLine(view: Pick<CaptureBeatView, 'stillWorking' | 'longerThanUsual'>): string | null {
  if (view.longerThanUsual) return CAPTURE_COPY.longerThanUsual;
  if (view.stillWorking) return CAPTURE_COPY.stillWorking;
  return null;
}

export function CaptureLoader({ view, firstName }: { view: CaptureBeatView; firstName: string }) {
  useEffect(() => {
    // Optional call: jsdom has no scrollTo.
    window.scrollTo?.({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  return (
    <JourneyScreen>
      <NarratedChecklist
        eyebrow={captureThanks(greetingName(firstName))}
        title={CAPTURE_COPY.title}
        intro={CAPTURE_COPY.intro}
        allDoneLine={CAPTURE_COPY.allDone}
        steps={CAPTURE_STEPS}
        phase={view.phase}
        waitLine={captureWaitLine(view)}
        missed={view.saved === false ? SAVE_MISSED : NOTHING_MISSED}
        focusTitle
      />
    </JourneyScreen>
  );
}
